/**
 * Frontend client for inbound "Shared with me" (server/modules/inboundshares).
 * Owner side: shareModelWith. Recipient side: listSharedWithMe + accept/dismiss.
 * Recipient calls are cross-workspace (keyed on the logged-in user's email).
 */
import { apiGet, apiPost, apiPatch } from "@/features/shared/apiClient";

export type ShareAccess = "view" | "download" | "comment";
export type ShareStatus = "pending" | "accepted" | "dismissed";

/** The populated model on an inbound share (server selects these fields). */
export interface SharedModel {
  _id: string;
  name: string;
  format: string;
  posterUrl?: string | null;
  posterKey?: string;
}

export interface InboundShare {
  _id: string;
  modelId: SharedModel;
  ownerWorkspaceId: { _id: string; name: string } | null;
  sharedByName: string;
  targetEmail: string;
  access: ShareAccess;
  message?: string;
  status: ShareStatus;
  createdAt: string;
  updatedAt: string;
}

/** Owner side — grant a model to a person by email. */
export const shareModelWith = (modelId: string, email: string, access: ShareAccess, message?: string) =>
  apiPost<InboundShare>("/inbound-shares", { modelId, email, access, ...(message ? { message } : {}) });

/** Recipient side — every non-dismissed grant addressed to me, across workspaces. */
export const listSharedWithMe = () => apiGet<InboundShare[]>("/inbound-shares/received");

export const acceptShare = (id: string) =>
  apiPatch<InboundShare>(`/inbound-shares/${id}`, { status: "accepted" });

export const dismissShare = (id: string) =>
  apiPatch<InboundShare>(`/inbound-shares/${id}`, { status: "dismissed" });
